import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { SecurityApiService } from '../http/security-api.service';
import {
  AuthenticatedUser,
  ResourceEnum,
  RightsEnum,
  UserGroup,
  UserGroupEnum,
} from './models';

/**
 * Service for managing user authorization and permissions.
 * Handles user groups, resource rights, and determines user home routes.
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly securityApiService = inject(SecurityApiService);

  private readonly _groups = signal<readonly UserGroup[] | null>(null);
  readonly groups = this._groups.asReadonly();

  private readonly _userHome = signal<string>('/');
  readonly userHome = this._userHome.asReadonly();

  private readonly _sysAdmin = signal<boolean>(false);
  /**
   * Signal that indicates whether the current user is a system administrator
   * (member of the SysAdmin user group). Reactive, safe to use in templates.
   */
  readonly isSysAdmin = this._sysAdmin.asReadonly();

  private userGroupList: readonly UserGroup[] | null = null;
  private resourceRights = new Map<ResourceEnum, RightsEnum>();

  /**
   * Computed signal that indicates whether groups have been loaded.
   */
  readonly hasGroups = computed(() => this._groups() !== null);

  /**
   * Sets up user authorization based on authenticated user data.
   * Calculates resource rights and determines the user's home route.
   * @param user - The authenticated user, or null for logout
   * @returns The user's home route path
   */
  setupUser(user: AuthenticatedUser | null): string {
    let home = '/';

    if (user && this.userGroupList) {
      const sysAdmin = user.appUser.userGroups?.some(
        (g) => g.userGroupId === UserGroupEnum.SysAdmin,
      ) ?? false;
      this._sysAdmin.set(sysAdmin);

      // Hierarchy inheritance: a user with a given UserGroup inherits the rights
      // of every numerically lower group (excluding SysAdmin, which is a separate tier).
      // Effect: AccountAdmin (5) inherits SurveyAdmin (4) + ClientAdmin (3) + Participant (2) + Rater (1).
      const assignedIds = user.appUser.userGroups
        ?.map((ug) => ug.userGroupId)
        .filter((id) => id !== UserGroupEnum.SysAdmin) ?? [];
      const maxId = assignedIds.length > 0 ? Math.max(...assignedIds) : -1;

      const groups = this.userGroupList.filter(
        (g) =>
          sysAdmin ||
          (g.userGroupId !== UserGroupEnum.SysAdmin && g.userGroupId <= maxId),
      );

      this.resourceRights.clear();

      groups
        .filter((g) => g.userGroupId !== UserGroupEnum.SysAdmin)
        .forEach((g) => {
          g.resources.forEach((r) => {
            const resource = r.resource.resourceId;
            if (!this.resourceRights.has(resource)) {
              this.resourceRights.set(resource, r.rightsMask);
            } else {
              const current = this.resourceRights.get(resource)!;
              this.resourceRights.set(resource, current | r.rightsMask);
            }
          });
        });

      if (this.hasRightsToResource(ResourceEnum.Admin)) {
        home = '/admin';
      } else if (this.hasRightsToResource(ResourceEnum.ClientAdmin)) {
        home = '/client';
      } else if (this.hasRightsToResource(ResourceEnum.Participant)) {
        home = '/participant';
      } else if (this.hasRightsToResource(ResourceEnum.Rater)) {
        home = '/rater';
      }

      this._groups.set(groups);
    } else {
      this._sysAdmin.set(false);
      this.resourceRights.clear();
      this._groups.set(null);
    }

    this._userHome.set(home);
    return home;
  }

  /**
   * Initializes the authorization service by loading user groups.
   * Caches the result for subsequent calls.
   * @returns Observable of user groups
   */
  initialize(): Observable<readonly UserGroup[]> {
    if (!this.userGroupList) {
      return this.securityApiService.getUserGroups().pipe(
        tap((result) => {
          this.userGroupList = result;
        }),
      );
    }
    return of(this.userGroupList);
  }

  /**
   * Checks if the current user has specific rights to a resource.
   * System admins automatically have all rights.
   * @param resource - The resource to check
   * @param rights - The rights level required (defaults to Read)
   * @returns True if user has the required rights
   */
  hasRightsToResource(resource: ResourceEnum, rights: RightsEnum = RightsEnum.Read): boolean {
    return this._sysAdmin() || (this.resourceRights.get(resource)! & rights) > 0;
  }

  /**
   * Gets the rights mask for a specific resource.
   * System admins receive All rights.
   * @param resource - The resource to query
   * @returns The rights mask for the resource
   */
  rightsToResource(resource: ResourceEnum): RightsEnum {
    if (this._sysAdmin()) {
      return RightsEnum.All;
    }
    return this.resourceRights.has(resource)
      ? this.resourceRights.get(resource)!
      : RightsEnum.None;
  }

  /**
   * Checks if the current user is a system administrator.
   * @returns True if user is a system admin
   */
  isAdmin(): boolean {
    return this._sysAdmin();
  }
}
